'use client';

import React, { useContext, useEffect, useState } from 'react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Modal } from '@tranquility-ai/theme-components/modal';
import { Switch } from '@tranquility-ai/theme-components/switch';
import { ToastContext } from '@/app/contexts';
import { ToastLevel } from '@/components/ui/toast';
import { useThreatReportSharing } from '@/hooks/useThreatReportSharing';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import { EdgeLambdaApiError, type ThreatReportSummary } from '@/shared/cors-edge-lambda-api';

export interface ThreatReportShareModalProps {
  caseId: string | undefined;
  report: ThreatReportSummary | null;
  onClose: () => void;
}

/**
 * Turn organization sharing on or off for one threat-report version.
 */
export function ThreatReportShareModal({ caseId, report, onClose }: ThreatReportShareModalProps) {
  const t = useTimePilotT();
  const { setToast, setToastOpen } = useContext(ToastContext);
  const sharing = useThreatReportSharing(caseId);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    setShared(report?.shared === true);
  }, [report]);

  const saving = sharing.isPending;

  const save = () => {
    if (!report || shared === report.shared) {
      onClose();
      return;
    }
    sharing.mutate(
      { report, shared },
      {
        onSuccess: () => {
          setToast({
            setOpen: setToastOpen,
            title: t(shared ? 'threatShare.shared' : 'threatShare.private'),
            description: t(shared ? 'threatShare.sharedDescription' : 'threatShare.privateDescription', {
              title: report.title,
            }),
            level: ToastLevel.SUCCESS,
            duration: 4000,
          });
          setToastOpen(true);
          onClose();
        },
        onError: (error) => {
          const status = error instanceof EdgeLambdaApiError ? error.status : null;
          const titleKey =
            status === 403
              ? 'threatShare.creatorOnly'
              : status === 409
                ? 'threatShare.completedOnly'
                : 'threatShare.updateFailed';
          setToast({
            setOpen: setToastOpen,
            title: t(titleKey),
            description: '',
            level: ToastLevel.ERROR,
            duration: 4000,
          });
          setToastOpen(true);
        },
      },
    );
  };

  return (
    <Modal
      isOpen={report != null}
      title={t('threatShare.title')}
      aria-label={t('threatShare.title')}
      size="sm"
      dismissOnBackdropClick={!saving}
      dismissOnEscape={!saving}
      onClose={saving ? undefined : onClose}
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button type="button" size="sm" style="text" color="light" background="inverse" disabled={saving} onClick={onClose}>
            {t('timeline.dialog.cancel')}
          </Button>
          <Button type="button" size="sm" style="solid" color="accent" disabled={saving} onClick={save}>
            {t('media.save')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 text-sm text-white/80">
        <p>
          {t('threatShare.body')}
        </p>
        <Switch
          checked={shared}
          disabled={saving}
          label={t('threatShare.shareWithOrg')}
          onCheckedChange={setShared}
          background="inverse"
        />
      </div>
    </Modal>
  );
}
